/**
 * Modelo y contratos del editor de flujos interactivos de WhatsApp (S4).
 *
 * Las formas de `IFlujoDefinicion` / `INodo` / `IOpcion` son una traducción
 * literal de `back/src/api/notificacion/services/whatsapp-flujo/tipos.ts` —
 * el backend es la fuente autoritativa de estos contratos (design.md,
 * "Interfaces / Contracts"). `PUT .../version/:versionId` reenvía este mismo
 * objeto tal cual al backend: no hay traducción snake_case/camelCase acá, la
 * definición viaja con las mismas claves de un lado a otro.
 *
 * El editor NUNCA reimplementa las reglas estructurales (alcanzabilidad,
 * terminación, handoff obligatorio): esas solo las valida
 * `POST .../validar` / `POST .../publicar` en el backend. Lo que este
 * archivo sí define son validadores de FORMA (requerido, longitud máxima)
 * para la experiencia de edición — la misma distinción que hace design.md
 * en "Editor architecture".
 */

// ---------------------------------------------------------------------------
// Tipos de nodo — cuatro y solo cuatro (spec: Flow Definition Model)
// ---------------------------------------------------------------------------

export const NODO_TIPO = {
    MESSAGE: 1,
    CHOICE: 2,
    HANDOFF: 3,
    END: 4,
} as const;

export type NodoTipo = (typeof NODO_TIPO)[keyof typeof NODO_TIPO];

/**
 * Nombre de cada tipo de paso. Lo comparten el editor de la plantilla y el
 * historial del flujo en la bandeja (`whatsapp-conversation-timeline`), así que
 * son frases nominales: tienen que leerse igual de bien como opción a elegir
 * ("¿qué hace este paso?") y como registro de lo que ya pasó. El verbo que
 * explica cada uno vive en NODO_TIPO_AYUDA, que solo muestra el editor.
 */
export const NODO_TIPO_LABEL: Record<NodoTipo, string> = {
    [NODO_TIPO.MESSAGE]: 'Mensaje',
    [NODO_TIPO.CHOICE]: 'Pregunta con botones',
    [NODO_TIPO.HANDOFF]: 'Derivación a una persona',
    [NODO_TIPO.END]: 'Cierre de la conversación',
};

/** Qué hace cada tipo, para el selector: el nombre solo no alcanza para elegir bien. */
export const NODO_TIPO_AYUDA: Record<NodoTipo, string> = {
    [NODO_TIPO.MESSAGE]: 'Le manda un texto y sigue solo al paso que elijas.',
    [NODO_TIPO.CHOICE]: 'Le manda un texto con botones y espera que toque uno.',
    [NODO_TIPO.HANDOFF]: 'Le avisa y deja la conversación en la bandeja para que la siga alguien del equipo.',
    [NODO_TIPO.END]: 'Le manda un último mensaje y termina. El bot no vuelve a contestar.',
};

export const NODO_TIPO_ICONO: Record<NodoTipo, string> = {
    [NODO_TIPO.MESSAGE]: 'pi pi-comment',
    [NODO_TIPO.CHOICE]: 'pi pi-list',
    [NODO_TIPO.HANDOFF]: 'pi pi-user',
    [NODO_TIPO.END]: 'pi pi-flag-fill',
};

/** Opciones para un `p-dropdown` de selector de tipo de nodo. */
export const NODO_TIPO_OPCIONES = Object.values(NODO_TIPO).map((valor) => ({
    label: NODO_TIPO_LABEL[valor],
    value: valor,
    ayuda: NODO_TIPO_AYUDA[valor],
}));

// ---------------------------------------------------------------------------
// Definición del flujo (el grafo autorado por el usuario no técnico)
// ---------------------------------------------------------------------------

export interface IOpcion {
    /** Se convierte en el id del botón interactivo que Meta envía y devuelve. */
    id: string;
    etiqueta: string;
    /**
     * Id de un nodo existente en `nodos`. En el editor SIEMPRE se elige de un
     * `p-dropdown` con los nodos ya creados (D9) — nunca se escribe a mano —
     * así una arista sin destino es prácticamente imposible de autorar por
     * accidente, en vez de solo detectable después.
     */
    destinoNodoId: string;
}

export interface INodo {
    id: string;
    tipo: NodoTipo;
    /** Puede contener placeholders `{{n}}`; se resuelven en el backend al enviar. */
    texto: string;
    /** Vacío salvo que `tipo === NODO_TIPO.CHOICE`. */
    opciones: IOpcion[];
    /** Solo tiene sentido cuando `tipo === NODO_TIPO.MESSAGE`. */
    siguienteNodoId: string | null;
}

export interface IFlujoDefinicion {
    nodoInicialId: string;
    nodos: INodo[];
}

// ---------------------------------------------------------------------------
// Validación estructural — la respuesta de `POST .../validar` y `.../publicar`
// ---------------------------------------------------------------------------

export const CODIGO_VALIDACION = {
    NODO_INICIAL_AUSENTE: 'NODO_INICIAL_AUSENTE',
    NODO_ID_DUPLICADO: 'NODO_ID_DUPLICADO',
    NODO_TIPO_NO_SOPORTADO: 'NODO_TIPO_NO_SOPORTADO',
    ARISTA_SIN_DESTINO: 'ARISTA_SIN_DESTINO',
    NODO_INALCANZABLE: 'NODO_INALCANZABLE',
    CAMINO_SIN_TERMINAL: 'CAMINO_SIN_TERMINAL',
    SIN_ARISTA_HANDOFF: 'SIN_ARISTA_HANDOFF',
    LIMITE_META_EXCEDIDO: 'LIMITE_META_EXCEDIDO',
} as const;

export type CodigoValidacion = (typeof CODIGO_VALIDACION)[keyof typeof CODIGO_VALIDACION];

export interface IHallazgoValidacion {
    codigo: CodigoValidacion;
    nodoId: string | null;
    opcionId: string | null;
    mensaje: string;
}

export interface IResultadoValidacion {
    valido: boolean;
    hallazgos: IHallazgoValidacion[];
}

// ---------------------------------------------------------------------------
// Registros CRUD — mismas formas que `IFlujoRegistro`/`IVersionRegistro`
// (back/.../flujo.service.ts)
// ---------------------------------------------------------------------------

export const ESTADO_VERSION_FLUJO = {
    BORRADOR: 1,
    PUBLICADO: 2,
    ARCHIVADO: 3,
} as const;

export type EstadoVersionFlujo = (typeof ESTADO_VERSION_FLUJO)[keyof typeof ESTADO_VERSION_FLUJO];

export const ESTADO_VERSION_FLUJO_LABEL: Record<EstadoVersionFlujo, string> = {
    [ESTADO_VERSION_FLUJO.BORRADOR]: 'Borrador',
    [ESTADO_VERSION_FLUJO.PUBLICADO]: 'Publicada',
    [ESTADO_VERSION_FLUJO.ARCHIVADO]: 'Archivada',
};

export interface IFlujoRegistro {
    id: number;
    idEmpresa: number;
    nombre: string;
    descripcion: string | null;
    activo: boolean;
    versionPublicadaId: number | null;
}

export interface IVersionRegistro {
    id: number;
    idEmpresa: number;
    flujoId: number;
    versionNumero: number;
    estadoCode: EstadoVersionFlujo;
    definicion: IFlujoDefinicion;
    publicadoPor: number | null;
    fechaPublicacion: string | null;
}

export interface IFlujoConVersiones {
    flujo: IFlujoRegistro;
    versiones: IVersionRegistro[];
}

// ---------------------------------------------------------------------------
// Registro de outcome del flujo (S5) — traducción literal de
// `IPasoRegistroLectura` en `back/.../whatsapp-flujo/repositorio.ts`. Lo
// devuelve `GET /whatsapp-conversation/:id/flujo`, un paso por fila,
// ordenado por `orden` ASC. `orden === 0` es el mensaje de campaña que
// arrancó el flujo (D6): tiene `nodoId`/`nodoTipoCode` del nodo inicial del
// flujo pero `mensajeDisparadorId: null` porque ningún inbound lo disparó.
// ---------------------------------------------------------------------------

export interface IPasoRegistroLectura {
    id: number;
    orden: number;
    nodoId: string;
    nodoTipoCode: number;
    textoMostrado: string | null;
    opcionId: string | null;
    opcionEtiqueta: string | null;
    mensajeDisparadorId: number | null;
    fechaOperacion: string | Date;
}

// ---------------------------------------------------------------------------
// Límites de Meta — SOLO para validación de FORMA en el editor (maxlength,
// UX). El backend (`meta-limites.ts`, doc-verificado) es quien realmente
// bloquea la publicación vía `LIMITE_META_EXCEDIDO`; estos números están acá
// para que el usuario vea el error ANTES de tocar "Validar", no para
// reemplazar esa verificación. Si algún día cambian, el backend sigue siendo
// la autoridad — este duplicado puede quedar desactualizado sin romper nada
// más que la UX temprana.
// ---------------------------------------------------------------------------

export const LIMITES_META_INTERACTIVO_UX = {
    BOTONES_MAX_REPLY: 3,
    BOTON_ETIQUETA_MAX_CARACTERES: 20,
    BOTON_ID_MAX_CARACTERES: 256,
    BODY_MAX_CARACTERES: 1024,
} as const;

/**
 * Permiso reutilizado de autoría de campañas (spec: Publish Permission
 * Parity — "No new, separate permission is introduced"). El backend
 * (`flujo.service.ts`, `PERMISO_AUTORIA_CAMPANA`) autoriza `publicar` contra
 * este mismo código; el front usa el mismo par módulo/registro para decidir
 * qué ve el menú y para ocultar el botón "Publicar" a quien no tiene acceso
 * — enforcement real sigue siendo 100% del backend, esto es solo UX.
 */
export const WHATSAPP_FLUJO_ACCESS = {
    RECORD_NAME: 'whatsapp_flujos',
    CODIGO_MODULO: 'notificacion',
    CODIGO_REGISTRO: 'historial_notificacion',
    NOMBRE_SINGULAR: 'Flujo de WhatsApp',
    NOMBRE_PLURAL: 'Flujos de WhatsApp',
} as const;

/** Nodo vacío por defecto al agregar uno nuevo en el editor. */
export const crearNodoVacio = (id: string, tipo: NodoTipo = NODO_TIPO.MESSAGE): INodo => ({
    id,
    tipo,
    texto: '',
    opciones: [],
    siguienteNodoId: null,
});

/** Opción vacía por defecto al agregar una opción a un nodo `CHOICE`. */
export const crearOpcionVacia = (id: string): IOpcion => ({
    id,
    etiqueta: '',
    destinoNodoId: '',
});
